import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { useLanguage } from '../shared/LanguageContext'
import { validatePortalToken } from '../shared/api'

function resolveDestination(userType, role, requestedDestination) {
  if (role === 'ta') return '/ta'
  // This is an allowlisted destination, never a caller-provided redirect URL.
  if (userType === 'parent' && requestedDestination === 'office-hours') return '/office-hours'
  return userType === 'parent' ? '/parent' : '/student'
}

export default function TokenAuth() {
  const [searchParams] = useSearchParams()
  const params = useParams()
  const navigate = useNavigate()
  const {
    setSession,
    setAuthData,
    isAuthenticated,
    userType,
    role,
    portalToken,
    loading,
    signOut,
  } = useAuth()
  const { t, setDefaultLanguage } = useLanguage()
  const [error, setError] = useState(null)
  const [validating, setValidating] = useState(true)
  const [validatedData, setValidatedData] = useState(null)
  const clearedFailureRef = useRef(null)

  useEffect(() => {
    if (loading || error || validatedData) return

    // A token in the current URL is authoritative. Only reuse restored auth
    // when there is no explicit token, or when the token is exactly the one
    // that produced the restored session.
    const onLandingPage = window.location.pathname.startsWith('/t/')
    const pathMatch = window.location.pathname.match(/^\/t\/([a-f0-9]+)$/)
    const token = params.token || searchParams.get('token') || (pathMatch && pathMatch[1])
    const hasExplicitToken = Boolean(params.token || pathMatch) || searchParams.has('token')
    const clearRestoredAuth = () => {
      const failureKey = `${hasExplicitToken ? 'explicit' : 'resolved'}:${token || ''}`
      if (clearedFailureRef.current === failureKey) return
      clearedFailureRef.current = failureKey
      void signOut()
    }

    if (isAuthenticated && userType && (!hasExplicitToken || token === portalToken)) {
      if (onLandingPage) {
        setValidatedData({ user_type: userType, role })
        setValidating(false)
      } else {
        const destination = resolveDestination(userType, role, searchParams.get('destination'))
        navigate(destination, { replace: true })
      }
      return
    }

    if (isAuthenticated && hasExplicitToken && token !== portalToken) {
      clearRestoredAuth()
    }

    if (!token) {
      if (hasExplicitToken) clearRestoredAuth()
      setError('invalidToken')
      setValidating(false)
      return
    }

    let cancelled = false

    async function validateToken() {
      try {
        const result = await validatePortalToken(token)
        if (cancelled) return

        const data = result?.data
        const isTa = data?.role === 'ta'
        const hasStudentScope = Array.isArray(data?.student_ids) && data.student_ids.length > 0
        const hasScope = isTa || hasStudentScope
        if (result?.code === 'rate_limited') {
          // #366: throttled, not invalid — keep any restored session and
          // show the calm retry state instead of "invalid link".
          setError('rateLimited')
          setValidating(false)
          return
        }
        if (result?.error || !data?.valid || !data?.user_type || !hasScope) {
          console.error('Token validation failed:', result)
          clearRestoredAuth()
          setError(result?.code === 'invalid_portal_token' ? 'invalidToken' : 'authError')
          setValidating(false)
          return
        }

        setDefaultLanguage(data.user_type === 'parent' ? 'cn' : 'en')

        // Store the validated auth data + portal token for subsequent API calls
        setAuthData({
          userType: data.user_type,
          role: data.role,
          studentIds: data.student_ids,
          classIds: data.class_ids,
          entityId: data.entity_id,
          portalToken: token,
        })
        // Mark as authenticated with a pseudo-session
        setSession({ validated: true, token })

        // Show landing page instead of auto-redirect
        // This lets parents favorite the /t/ URL before navigating
        setValidatedData(data)
        setValidating(false)

      } catch (err) {
        if (!cancelled) {
          console.error('Token validation failed:', err)
          clearRestoredAuth()
          setError('authError')
          setValidating(false)
        }
      }
    }

    validateToken()

    return () => { cancelled = true }
  }, [
    searchParams,
    params.token,
    navigate,
    setSession,
    setAuthData,
    setDefaultLanguage,
    isAuthenticated,
    userType,
    role,
    portalToken,
    loading,
    signOut,
    error,
    validatedData,
  ])

  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-icon auth-icon-error">!</div>
          <p className="auth-error-text">{t(error)}</p>
          <button
            className="auth-button"
            onClick={() => {
              setError(null)
              setValidating(true)
              window.location.reload()
            }}
          >
            {t('retry')}
          </button>
        </div>
      </div>
    )
  }

  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="spinner" />
          <p className="auth-loading-text">{t('authenticating')}</p>
        </div>
      </div>
    )
  }

  // Landing page — shown after successful validation
  // Parents can favorite THIS page (the /t/ URL) before entering the portal
  if (validatedData) {
    const isParent = validatedData.user_type === 'parent'
    const isTa = !isParent && validatedData.role === 'ta'
    const destination = resolveDestination(validatedData.user_type, validatedData.role, searchParams.get('destination'))
    const isOfficeHours = destination === '/office-hours'

    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--accent-blue)', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '20px', fontWeight: 800 }}>M</div>
          <h2 style={{ margin: '0 0 4px', fontSize: '20px', color: 'var(--text-primary)', fontWeight: 700 }}>
            Matt SD
          </h2>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
            {isOfficeHours ? '预约免费辅导 · Free Office Hours' : isParent ? '查看孩子的学习进度' : isTa ? '进入助教反馈入口' : '查看你的学习进度'}
          </p>
          <button
            className="auth-button"
            onClick={() => navigate(destination, { replace: true })}
            style={{ width: '100%', padding: '14px', fontSize: '16px', borderRadius: '8px', background: 'var(--accent-blue)', color: 'white', border: 'none', cursor: 'pointer' }}
          >
            {isOfficeHours ? '预约辅导 · Book Office Hours' : isParent ? '查看进度' : isTa ? '进入助教入口' : '进入'}
          </button>
          <p style={{ margin: '16px 0 0', fontSize: '11px', color: 'var(--text-tertiary)' }}>
            💡 收藏此页面以便下次快速访问
            <br />
            Tip: Save this page to Favorites for quick access next time
          </p>
        </div>
      </div>
    )
  }

  return null
}
